import { CurrencyPipe, PercentPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';

import { ContractMovementType, IContractCurrentSituation, IContractMovement, IGenericContract, IKpiItem } from 'src/app/models/generic';
import { ApiContractService } from 'src/app/services/api/api-contract.service';
import { StatusBadgeTone } from 'src/app/shared/components/status-badge/status-badge.component';

@Component({
  selector: 'app-contrato-financiamento',
  templateUrl: './contrato-financiamento.component.html',
  styleUrls: ['./contrato-financiamento.component.scss']
})
export class ContratoFinanciamentoComponent implements OnInit {
  contractData!: IGenericContract;
  kpis: IKpiItem[] = [];
  movements: IContractMovement[] = [];
  currentSituation: IContractCurrentSituation | null = null;
  saldoFaturado = '-';
  comissaoVigente = '-';
  isLoading = true;

  constructor(
    private _contractService: ApiContractService,
    private _currencyPipe: CurrencyPipe,
    private _percentPipe: PercentPipe
  ) {}

  ngOnInit(): void {
    this.contractData = this._contractService.contractData;

    const contractId = this.contractData?.ContractId;

    forkJoin({
      movements: this._contractService.getContractMovements(contractId),
      currentSituation: this._contractService.getContractCurrentSituation(contractId)
    }).subscribe({
      next: ({ movements, currentSituation }) => {
        this.movements = movements.Data ?? [];
        this.buildKpis();
        this.currentSituation = currentSituation.Data ?? null;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private buildKpis(): void {
    const faturadoValor = this.sumByType('Faturamento', 'Valor');
    const estornadoValor = this.sumByType('Estorno', 'Valor');
    const faturadoComissao = this.sumByType('Faturamento', 'Comissao');
    const estornadoComissao = this.sumByType('Estorno', 'Comissao');

    this.saldoFaturado = this.currency(faturadoValor - estornadoValor);
    this.comissaoVigente = this.currency(faturadoComissao - estornadoComissao);

    this.kpis = [
      {
        label: 'Faturado bruto',
        value: this.currency(faturadoValor),
        hint: 'Soma dos faturamentos emitidos'
      },
      {
        label: 'Estornos',
        value: this.currency(-estornadoValor),
        tone: estornadoValor ? 'danger' : 'default',
        hint: 'Valor devolvido ao banco'
      },
      {
        label: 'Faturado líquido',
        value: this.currency(faturadoValor - estornadoValor),
        tone: 'brand',
        hint: 'Base vigente para comissão'
      }
    ];
  }

  private sumByType(tipo: ContractMovementType, field: 'Valor' | 'Comissao' = 'Valor'): number {
    // "Refaturamento" e um faturamento de verdade (so que substitui uma versao anterior) --
    // conta junto com "Faturamento" nos KPIs, senao o Faturado Bruto fica sub-representado.
    return this.movements
      .filter((m) => (tipo === 'Faturamento' ? m.Tipo !== 'Estorno' : m.Tipo === tipo) && !this.isLoteCancelado(m))
      .reduce((total, m) => total + (m[field] ?? 0), 0);
  }

  // Tentativas de lote canceladas aparecem na tabela para fins historicos, mas nao
  // representam faturamento/estorno real -- nao devem entrar na soma dos KPIs.
  isLoteCancelado(movement: IContractMovement): boolean {
    return (movement.LoteStatus || '').toLowerCase().includes('cancelad');
  }

  private currency(value: number): string {
    return this._currencyPipe.transform(value, 'BRL') ?? '-';
  }

  movementTypeTone(tipo: ContractMovementType): StatusBadgeTone {
    if (tipo === 'Estorno') return 'danger';
    if (tipo === 'Refaturamento') return 'warning';
    return 'info';
  }

  movementTone(movement: IContractMovement): StatusBadgeTone {
    const status = (movement.Status || '').toLowerCase();

    if (status.includes('cancelad')) return 'danger';
    if (status.includes('registrad')) return 'success';
    if (status.includes('elaborado')) return 'warning';

    return 'neutral';
  }

  formatPercent(value?: number): string {
    return value != null ? this._percentPipe.transform(value / 100, '1.0-2') ?? '-' : '-';
  }
}
